import { useTranslation } from 'react-i18next';

import type { PickerRow } from '@nucleus-commerce/admin-kit/resource-picker';
import type {
  Money,
  ProductListItem,
  ProductStatus,
  StoreProductListItem,
  StoreProductStatus,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { PickerStatusBadge } from '../components/picker-status-badge';

/** A row of `GET /org/products`. */
export type OrgProductRow = ProductListItem;
/** A row of `GET /stores/:id/products`, keyed by its master `productId` in the pickers. */
export type StoreProductRow = StoreProductListItem;

export interface ProductRowOptions {
  /** Locks the row, ticked: the caller's list already holds it. */
  locked?: boolean;
  /** What a locked row (or a `memberOf` member) says instead of "Already added". */
  lockedLabel?: string | undefined;
}

interface ProductFields {
  vendor: string | null;
  variantCount: number;
  featuredImage?: { url: string; alt: string | null } | null;
  priceRange?: { min: Money; max: Money } | null;
}

interface MemberFields {
  isMember?: boolean;
  memberReason?: 'member' | 'owned_elsewhere' | null;
}

/**
 * Builds picker rows for products: thumbnail, title, "Vendor · 3 variants", the status badge, the
 * price range and, for a store's rows, the available stock. Rows the target already holds
 * (`lockedIds`, or `isMember` from `memberOf`) are locked, ticked, with the reason.
 */
export function useProductRow() {
  const { t, i18n } = useTranslation('resourcePickers');
  const format = useFormat();
  const number = new Intl.NumberFormat(i18n.language);

  const price = (range: ProductFields['priceRange']) => {
    if (!range) return null;
    const min = format.money(range.min);
    return range.min.amount === range.max.amount ? min : `${min} – ${format.money(range.max)}`;
  };
  const lock = (row: MemberFields, options: ProductRowOptions): Partial<PickerRow> => {
    if (row.isMember && row.memberReason === 'owned_elsewhere') {
      return { disabled: t('inAnotherSchedule'), checked: true };
    }
    if (row.isMember || options.locked) {
      return { disabled: options.lockedLabel ?? t('alreadyAdded'), checked: true };
    }
    return {};
  };
  const build = (
    product: ProductFields,
    title: string,
    status: ProductStatus | StoreProductStatus,
    member: MemberFields,
    options: ProductRowOptions,
    stock?: string | null,
  ): PickerRow => {
    const details = [
      product.vendor,
      product.variantCount > 1 ? t('variantCount', { count: product.variantCount }) : null,
    ].filter(Boolean);
    const shownPrice = price(product.priceRange);
    return {
      title,
      description: details.length > 0 ? details.join(' · ') : undefined,
      thumbnail: product.featuredImage
        ? { url: product.featuredImage.url, alt: product.featuredImage.alt }
        : null,
      badge: <PickerStatusBadge status={status} />,
      meta:
        stock || shownPrice ? (
          <>
            {stock ? <span className="text-xs">{stock}</span> : null}
            {shownPrice ? <span>{shownPrice}</span> : null}
          </>
        ) : null,
      ...lock(member, options),
    };
  };
  const stockOf = (available: number | null) =>
    available === null
      ? null
      : available > 0
        ? t('stock.available', { count: available, number: number.format(available) })
        : t('stock.out');

  return {
    org: (row: OrgProductRow, options: ProductRowOptions = {}): PickerRow =>
      build(row, row.title, row.status, row, options),
    store: (row: StoreProductRow, options: ProductRowOptions = {}): PickerRow =>
      build(
        row.product,
        row.title ?? row.product.title,
        row.status,
        row,
        options,
        stockOf(row.available ?? null),
      ),
  };
}
